import React from "react";
import { useTranslation } from "react-i18next";
import {
  BuiltInFurnitureMaterial,
} from "../../../../features/floorplan3d/materials/builtInFurnitureMaterials";
import {
  BuiltInMaterialCategoryGroup,
  getBuiltInMaterialSurfaceMapping,
  listVisibleBuiltInFurnitureMaterialsByCategory,
} from "../../../../features/floorplan3d/materials/builtInMaterialSurfaceMappings";
import { CatalogImageOwner } from "../../../../features/floorplan3d/catalog/catalogQueries";
import {
  ProductSelectOptions,
  ProductsPanel,
} from "../../ProductsPanel";

export type CatalogBrowseProps = {
  intent: "wallpaperProduct" | "flooringProduct";
  /** Category chip key, e.g. `catalog:wallpaper`. */
  categoryKey: string;
  label: string;
  onProductSelect: (
    product: CatalogImageOwner,
    options?: ProductSelectOptions,
  ) => void;
};

type BuiltInMaterialsPanelProps = {
  selectedMaterialId?: string;
  initialCategoryKey?: string | null;
  /** Keep the property-panel category chip in sync when changing filters here. */
  onCategoryChange?: (key: string | null) => void;
  /** When walls/floors are selected, Wallpaper/Flooring appears as a category chip. */
  catalogBrowse?: CatalogBrowseProps | null;
  onClose: () => void;
  onSelect: (material: BuiltInFurnitureMaterial) => void;
};

/**
 * Browse the admin Ready materials pack (including procedural metal/glass).
 * Wallpaper/Flooring can appear as an extra category chip next to Stone when
 * walls or floors are selected.
 */
const BuiltInMaterialsPanel: React.FC<BuiltInMaterialsPanelProps> = ({
  selectedMaterialId,
  initialCategoryKey = null,
  onCategoryChange,
  catalogBrowse = null,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const categoryGroups = React.useMemo(
    () => listVisibleBuiltInFurnitureMaterialsByCategory(),
    [],
  );
  const [categoryKey, setCategoryKey] = React.useState<string | null>(
    initialCategoryKey,
  );
  const [search, setSearch] = React.useState("");

  const selectCategory = (key: string | null) => {
    setCategoryKey(key);
    onCategoryChange?.(key);
  };

  const showingCatalog =
    Boolean(catalogBrowse) && categoryKey === catalogBrowse?.categoryKey;

  const materials = React.useMemo(() => {
    if (showingCatalog) return [];
    const source = categoryKey
      ? (categoryGroups.find((group) => group.categoryKey === categoryKey)
          ?.materials ?? [])
      : categoryGroups.flatMap((group) => group.materials);
    const query = search.trim().toLowerCase();
    if (!query) return source;
    return source.filter((material) => {
      const label = (
        material.label ||
        (material.labelKey ? t(material.labelKey) : material.id)
      ).toLowerCase();
      const category = (
        material.categoryName ??
        material.families[0] ??
        ""
      ).toLowerCase();
      return label.includes(query) || category.includes(query);
    });
  }, [categoryGroups, categoryKey, search, showingCatalog, t]);

  return (
    <div
      className="fixed z-50 flex max-h-[72vh] min-h-80 w-[360px] flex-col overflow-hidden rounded border border-gray-300 bg-white shadow-lg"
      style={{ top: 96, right: 252 }}
    >
      <div className="flex h-11 items-center justify-between border-b border-gray-300 px-3">
        <div className="text-sm font-medium">
          {t("propertyPanel.browseMaterials")}
        </div>
        <button
          type="button"
          className="h-8 w-8 rounded-sm text-lg leading-none text-gray-500 hover:bg-gray-100"
          onClick={onClose}
          aria-label={t("panels.closeCatalogBrowser")}
        >
          ×
        </button>
      </div>
      <div className="space-y-2 border-b border-gray-200 p-3">
        {!showingCatalog && (
          <input
            className="h-9 w-full rounded-sm border border-gray-300 px-2 text-sm outline-none focus:border-blue-600"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("propertyPanel.searchMaterials")}
          />
        )}
        <CategoryChipRow
          groups={categoryGroups}
          catalogChip={
            catalogBrowse
              ? {
                  key: catalogBrowse.categoryKey,
                  label: catalogBrowse.label,
                }
              : null
          }
          activeKey={categoryKey}
          onSelect={selectCategory}
        />
      </div>
      {showingCatalog && catalogBrowse ? (
        <ProductsPanel
          embedded
          intent={catalogBrowse.intent}
          onClose={onClose}
          onProductSelect={catalogBrowse.onProductSelect}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {materials.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              {t("common.noResults")}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {materials.map((material) => {
                const isSelected = selectedMaterialId === material.id;
                const mapping = getBuiltInMaterialSurfaceMapping(material.id);
                const imageUrl = mapping?.imageUrl ?? material.textureUrl;
                const label =
                  material.label ||
                  (material.labelKey ? t(material.labelKey) : material.id);
                return (
                  <button
                    key={material.id}
                    type="button"
                    aria-pressed={isSelected}
                    className={`overflow-hidden rounded-sm border text-left ${
                      isSelected
                        ? "border-gray-800 ring-1 ring-gray-800"
                        : "border-gray-200 hover:border-gray-400"
                    }`}
                    onClick={() => {
                      onSelect(material);
                      onClose();
                    }}
                  >
                    <div className="relative flex aspect-square items-center justify-center bg-gray-100">
                      {imageUrl ? (
                        <span
                          className="h-full w-full bg-cover bg-center"
                          style={{ backgroundImage: `url(${imageUrl})` }}
                        />
                      ) : (
                        <span
                          className="h-full w-full"
                          style={{
                            backgroundColor: `#${material.color
                              .toString(16)
                              .padStart(6, "0")}`,
                          }}
                        />
                      )}
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="truncate text-xs font-medium text-gray-900">
                        {label}
                      </div>
                      <div className="truncate text-[11px] text-gray-500">
                        {material.categoryName ??
                          material.families[0] ??
                          t("propertyPanel.materials")}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CategoryChipRow: React.FC<{
  groups: BuiltInMaterialCategoryGroup[];
  catalogChip?: { key: string; label: string } | null;
  activeKey: string | null;
  onSelect: (key: string | null) => void;
}> = ({ groups, catalogChip = null, activeKey, onSelect }) => {
  const { t } = useTranslation();
  const chips: Array<{ key: string | null; label: string }> = [
    { key: null, label: t("propertyPanel.materialCategoryAll") },
    ...groups.map((group) => ({
      key: group.categoryKey,
      label: group.categoryLabel,
    })),
  ];
  if (catalogChip) {
    chips.push({ key: catalogChip.key, label: catalogChip.label });
  }

  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((chip) => {
        const isActive = activeKey === chip.key;
        return (
          <button
            key={chip.key ?? "all"}
            type="button"
            className={`rounded-sm border px-2 py-0.5 text-[11px] ${
              isActive
                ? "border-gray-800 bg-gray-800 text-white"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => onSelect(chip.key)}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
};

export default BuiltInMaterialsPanel;
